import React, { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getPreviousQuote, upsert, calcule, QuotationResponse, QuoteRequest } from '../api/quoteService';
import { getApplicationByQuoteId } from '../api/applicationService';
import {
  isApplicationPath,
  isQuotePath,
  quoteWriteModeFor,
  QuoteWriteMode,
  shouldSkipBootCalcule,
  shouldSkipBootUpsert,
} from './quoteLoadGuard';
import { REACT_APP_COUNTRY, REACT_APP_QUOTATION_API_KEY, REACT_APP_QUOTE_VERSION_BR } from '../config';
import { captureMessageWithQuoteId } from '../utils/sentry';
import { MARKETS } from '../quoter/markets';
import { PRODUCT_RULES } from '../quoter/domain/rules/product';
import type { ProductRuleName } from '../quoter/contract/market';

// INT prices easy_term up to 5M (quotation_validate.go); deriving it with the 1M legacy cut sent
// master_term for a 4M quote and the engine answered 400 on load. MX and BR keep the legacy rule.
const PRODUCT_RULE_BY_DEPLOY: Record<string, ProductRuleName> = { INT: 'coverageBands' };

type CoverageData = {
  amount: number;
  price: number | null;
};

type SpecialPage = {
  group: string;
  template: string;
  pageReference: number;
}

export type Dependent = {
  id: string;
  name: string;
  lastName: string;
  relationship: string;
  pages: string[];
  specialPages: SpecialPage[];
}

export type Beneficiary = {
  name: string;
  relationship: string;
  percentage: number;
}

export interface RefreshedQuote {
  quote: QuotationResponse;
  selected: QuoteRequest;
  lastQuote: any;
}

interface QuoteContextType {
  quoteID: string;
  quote: any;
  calculation: (formData: any) => void;
  selected: any;
  lastQuote: any;
  product: string;
  defaultALoD: CoverageData;
  defaultAd: CoverageData;
  defaultTpd: CoverageData;
  amountInclude: number;
  loading: boolean;
  setLoading: (value: boolean) => void;
  moreBenefits: boolean;
  setMoreBenefits?: (value: boolean) => void;
  error: string | null;
  calculationError: boolean;
  dismissCalculationError: () => void;
  indexOfPages: number[];
  setIndexOfPages?: (value: number[]) => void;
  specialPages?: SpecialPage[];
  setSpecialPages?: (value: SpecialPage[]) => void;
  dependents?: Dependent[];
  setDependents?: (value: Dependent[]) => void;
  beneficiaries?: Beneficiary[];
  setBeneficiaries?: (value: Beneficiary[]) => void;
  redirectPageId: number | null;
  setRedirectPageId: (pageId: number | null) => void;
  startFromBeginning: boolean;
  setStartFromBeginning: (value: boolean) => void;
  isTest: boolean;
  setIsTest: (value: boolean) => void;
  isTestLoading: boolean;
  refreshQuote: () => Promise<RefreshedQuote | null>;
  ridersOverLimit: boolean;
  setRidersOverLimit: (value: boolean) => void;
}

const BR_RIDERS_TOTAL_LIMIT = 500000;

const isRealDate = (value?: string) => !!value && !value.startsWith('0001-01-01');

const JSON_VERSIONS: any = {
  'easy_term': {
    'int': 'v3',
    'br': REACT_APP_QUOTE_VERSION_BR,
    'mx': 'v1',
  },
  'master_term': {
    'int': 'v2',
    'mx': 'v1',
  },
}

export const QuoteContext = createContext<QuoteContextType>({
  quoteID: '',
  quote: null,
  calculation: () => { },
  selected: null,
  lastQuote: null,
  product: '',
  defaultALoD: {
    amount: 0,
    price: 0,
  },
  defaultAd: {
    amount: 0,
    price: 0,
  },
  defaultTpd: {
    amount: 0,
    price: 0,
  },
  amountInclude: 0,
  setMoreBenefits: () => { },
  loading: false,
  setLoading: () => { },
  moreBenefits: false,
  error: null,
  calculationError: false,
  dismissCalculationError: () => { },
  indexOfPages: [],
  setIndexOfPages: () => { },
  specialPages: [],
  setSpecialPages: () => { },
  dependents: [],
  setDependents: () => { },
  beneficiaries: [],
  setBeneficiaries: () => { },
  redirectPageId: null,
  setRedirectPageId: () => { },
  startFromBeginning: false,
  setStartFromBeginning: () => { },
  isTest: false,
  setIsTest: () => { },
  isTestLoading: true,
  refreshQuote: async () => null,
  ridersOverLimit: false,
  setRidersOverLimit: () => { },
});

interface QuoteProviderProps {
  children: ReactNode;
}

export const QuoteProvider: React.FC<QuoteProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [quoteID, setQuoteID] = useState<string>('');
  const [quote, setQuote] = useState<QuotationResponse>();
  const [selected, setSelected] = useState<QuoteRequest>();
  const [lastQuote, setLastQuote] = useState<any>();
  const [product, setProduct] = useState<string>('easy_term');
  const [amountInclude, setAmountInclude] = useState<number>(0);
  const [moreBenefits, setMoreBenefits] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calculationError, setCalculationError] = useState(false);
  const latestCalculationRef = useRef(0);
  // Bumped synchronously when an edit starts, before any await. The deferred
  // boot write reads the quote after an await; an edit in that window is newer
  // and must not be overwritten by the body just read.
  const quoteEditEpochRef = useRef(0);
  const mountedRef = useRef(true);
  // desconocido until the application read settles. A failed read is not
  // cached: boot writes stay skipped, and the next edit reads again.
  // Reset when the quote id changes: the previous mode must not apply to the
  // next quote, and calculation() waits on the shared read while it is unknown.
  const quoteLockedRef = useRef<QuoteWriteMode>('desconocido');
  const activeQuoteIdRef = useRef('');
  const quoteWriteReadRef = useRef<{ quoteId: string; promise: Promise<QuoteWriteMode> } | null>(null);
  // Boot calcule/upsert skipped because this quote loaded on /application.
  // Anterior and backToQuoter navigate to /quote without remounting
  // QuoteProvider. Only the id is kept: /confirmation calls refreshQuote, and
  // storing the request would upsert that opening snapshot over the quote.
  const pendingBootRef = useRef<{ quoteId: string } | null>(null);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const runBootWritesRef = useRef<
    (
      id: string,
      request: QuoteRequest,
      response: QuotationResponse,
      productName: string,
      includeMxUpsert: boolean,
      stillCurrent?: () => boolean,
    ) => Promise<void>
  >(async () => {});
  const runDeferredBootRef = useRef<(id: string) => Promise<void>>(async () => {});
  const deferredBootRef = useRef<Promise<void> | null>(null);
  const [searchParams] = useSearchParams();
  const [defaultALoD, setDefaultALoD] = useState<CoverageData>({
    amount: 0,
    price: 0,
  });
  const [defaultAd, setDefaultAd] = useState<CoverageData>({ amount: 0, price: 0 });
  const [defaultTpd, setDefaultTpd] = useState<CoverageData>({ amount: 0, price: 0 });
  const [indexOfPages, setIndexOfPages] = useState<number[]>([]);
  const [specialPages, setSpecialPages] = useState<SpecialPage[]>([]);
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [redirectPageId, setRedirectPageId] = useState<number | null>(null);
  const [startFromBeginning, setStartFromBeginning] = useState<boolean>(
    localStorage.getItem('startFromBeginning') === 'true'
  );
  const [isTest, setIsTest] = useState<boolean>(false);
  const [isTestLoading, setIsTestLoading] = useState<boolean>(true);
  const [ridersOverLimit, setRidersOverLimit] = useState<boolean>(false);

  const isSpecialCountry = (country: string) => {
    return country === 'CL' || country === 'AR' || country === 'UY';
  };

  const getProduct = (insuredAmount: number, country: string) =>
    PRODUCT_RULES[PRODUCT_RULE_BY_DEPLOY[REACT_APP_COUNTRY] ?? 'legacyThreshold']({
      insuredAmount,
      residenceCountry: country,
      easyTermMax: MARKETS.INT.mainCoverage.easyTermMax,
    });

  const getTypeQuote = (country: string) => {
    if (country === 'BR') {
      return 'br';
    } else if (country === 'MX') {
      return 'mx';
    } else {
      return 'int';
    }
  }

  // Función helper para limpiar additional_data si es null antes de enviar al upsert
  const cleanLeadForUpsert = (lead: any) => {
    if (!lead) return lead;
    const cleanedLead = { ...lead };
    if (cleanedLead.additional_data === null || cleanedLead.additional_data === undefined) {
      delete cleanedLead.additional_data;
    }
    return cleanedLead;
  }
  // Only the quote id loads the quote. One-shot params (`fix`, `step`, `field`)
  // are removed after a deep link; refetching on that change sets `loading` and
  // unmounts the chat or the wizard, which aborts the correction in flight.
  const quoteIdFromUrl = searchParams.get('quoteID');

  // One status read per quote id, shared by fetchQuote and calculation().
  // A read started for a quote that is no longer current must not publish
  // its mode or replace the next quote's in-flight read.
  // `force` starts a new read and bypasses the application GET cache. The
  // deferred /quote write uses it: the mode from opening /application is stale
  // after /confirmation.
  const beginQuoteWriteRead = (id: string, force = false): Promise<QuoteWriteMode> => {
    if (activeQuoteIdRef.current !== id) {
      return Promise.resolve('desconocido');
    }
    const current = quoteWriteReadRef.current;
    if (!force && current && current.quoteId === id) return current.promise;

    quoteLockedRef.current = 'desconocido';
    const published: { promise: Promise<QuoteWriteMode> | null } = { promise: null };
    const promise = (async () => {
      try {
        const application = await getApplicationByQuoteId(id, force ? { fresh: true } : undefined);
        const status =
          application?.found && application.data?.status ? application.data.status : undefined;
        const mode = quoteWriteModeFor({ readFailed: false, status });
        if (quoteWriteReadRef.current?.promise === published.promise) quoteLockedRef.current = mode;
        return mode;
      } catch {
        if (quoteWriteReadRef.current?.promise === published.promise) {
          quoteLockedRef.current = 'desconocido';
          quoteWriteReadRef.current = null;
        }
        return 'desconocido';
      }
    })();
    published.promise = promise;
    quoteWriteReadRef.current = { quoteId: id, promise };
    return promise;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (quoteIdFromUrl) {
      activeQuoteIdRef.current = quoteIdFromUrl;
      quoteLockedRef.current = 'desconocido';
      quoteWriteReadRef.current = null;
      pendingBootRef.current = null;
      deferredBootRef.current = null;
      setQuoteID(quoteIdFromUrl);
      fetchQuote(quoteIdFromUrl);
      syncApplicationInfo(quoteIdFromUrl);
      //setZone(isBR ? 'BR' : 'INT');
    } else {
      alert('No se ha encontrado el ID de la cotización');
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteIdFromUrl]);

  // Anterior and backToQuoter leave for /quote without remounting QuoteProvider
  // or rerunning fetchQuote. The boot calcule and the MX upsert were skipped
  // on /application; run them once here, from a new read. The mode and the
  // body captured while opening /application are stale once /confirmation has
  // called refreshQuote.
  useEffect(() => {
    if (!isQuotePath(location.pathname)) return;
    const pending = pendingBootRef.current;
    if (!pending || pending.quoteId !== activeQuoteIdRef.current) return;
    pendingBootRef.current = null;
    const boot = runDeferredBootRef.current(pending.quoteId);
    deferredBootRef.current = boot;
    // A missed re-read, an unknown status, or leaving /quote restores
    // pendingBootRef and resolves. This is the unexpected failure after that
    // (the price preview throw). Do not retry it: the upsert may already
    // have landed, and the quote on screen must stay.
    void boot.catch((error) => {
      captureMessageWithQuoteId('Deferred quote boot failed', {
        level: 'error',
        extra: { error: error instanceof Error ? error.message : String(error) },
        tags: { error_type: 'deferred_boot_failed' },
      });
    }).finally(() => {
      if (deferredBootRef.current === boot) deferredBootRef.current = null;
    });
  }, [location.pathname]);

  const syncApplicationInfo = async (id: string) => {
    try {
      const response = await getApplicationByQuoteId(id);
      if (response.found && response.data && response.data.is_test != null) {
        setIsTest(response.data.is_test);
      }
    } catch (error) {
      console.error('Error syncing application info:', error);
    } finally {
      setIsTestLoading(false);
    }
  };

  // The api-client application row only exists once the wizard has started, so on a
  // fresh quote `syncApplicationInfo` finds nothing and `isTest` would stay false for
  // the whole session — making confirmation/summary attempt the PDF + legal S3 upload
  // for a test quote. The quotation service is the source of truth for the flag.
  const syncIsTestFromQuote = (quoteIsTest?: boolean) => {
    if (quoteIsTest == null) return;
    setIsTest(quoteIsTest);
    setIsTestLoading(false);
  };

  const buildQuoteRequest = (response: QuotationResponse, product: string): QuoteRequest => {
    const q = response.quotations.find((q) => q.selected);
    const cp_protections = q?.protections?.filter((p) => p.total_cp_in_frecuency) || [];
    const ci_protections = q?.protections?.filter((p) => p.total_ci_in_frecuency) || [];

    return {
      version: JSON_VERSIONS[product][getTypeQuote(response.country)],
      product_type: product,
      country: response.country,
      country_origin: response.country_origin,
      term: response.term,
      term_selected: q?.term || response.term,
      frecuency: response.frecuency,
      frecuency_selected: q?.frecuency || response.frecuency,
      gender: response.gender,
      smoker: response.smoker,
      age: response.age,
      policy_amount: response.insured_amount,
      add_amount: response.insured_add_amount === 0 ? null : response.insured_add_amount,
      ad_amount: response.insured_ad_amount === 0 ? null : response.insured_ad_amount || null,
      tpd_amount: response.insured_tpd_amount === 0 ? null : response.insured_tpd_amount || null,
      extra_premium_table_ad: response.extra_premium_table_add,
      rop: response.rop,
      lead: cleanLeadForUpsert(response.lead),
      annual_income: response.annual_income,
      income_replacement: response.income_replacement,
      protection: response.protection,
      cp_protections: cp_protections,
      ci_protections: ci_protections,
      extra_premium_table_add: response.extra_premium_table_add,
      extra_premium_table_rating: response.extra_premium_table_rating,
      flat_extra_amount: response.flat_extra_amount,
      flat_extra_years: response.flat_extra_years,
      coverage_include_amount: response.coverage_include_amount,
      score: response.score,
      is_test: response.is_test,
    };
  };

  const runBootWrites = async (
    id: string,
    request: QuoteRequest,
    response: QuotationResponse,
    productName: string,
    includeMxUpsert: boolean,
    stillCurrent?: () => boolean,
  ) => {
    // The deferred boot passes epoch + active quote + mounted. A direct load
    // only has the quote id. Either way, a write that resumed after the quote
    // moved on must not publish.
    const stale = () => (stillCurrent ? !stillCurrent() : activeQuoteIdRef.current !== id);

    if (includeMxUpsert) {
      try {
        const cleanedRequest = {
          ...request,
          lead: cleanLeadForUpsert(request.lead),
        };
        const upsertResult = await upsert(id, cleanedRequest, REACT_APP_QUOTATION_API_KEY);
        if (stale()) return;
        setQuote({
          ...upsertResult,
          created_at: isRealDate(upsertResult.created_at) ? upsertResult.created_at : response.created_at,
        });
        setLastQuote(upsertResult.quotations.find((q) => q.selected));

        // El GET (getPreviousQuote) NO trae el catálogo `protections`; el upsert SÍ.
        // Si tras recalcular la protección seleccionada quedó sin tarifas (p. ej. la edad
        // del asegurado la bloquea), la limpiamos del estado para no mostrarla
        // "seleccionada sin costo" (BRAZ-426). Sólo afecta la vista, no re-persistimos.
        const upsertSelected = upsertResult.quotations.find((q) => q.selected);
        const upsertCp = upsertSelected?.protections?.filter((p) => p.total_cp_in_frecuency) || [];
        const upsertCi = upsertSelected?.protections?.filter((p) => p.total_ci_in_frecuency) || [];
        if (request.protection && upsertCp.concat(upsertCi).length === 0) {
          setSelected({ ...request, protection: null });
        }
      } catch (upsertError) {
        // No fallar todo el proceso si el upsert falla, solo log el error
      }
    }

    if (stale()) return;
    const requestCopy = { ...request };
    requestCopy.add_amount = REACT_APP_COUNTRY === 'MX' ? 50000 : 100000;//TODO: refactor
    requestCopy.product_type = productName;
    requestCopy.lead = cleanLeadForUpsert(requestCopy.lead);
    const quotationTemp = await calcule(id, requestCopy, REACT_APP_QUOTATION_API_KEY);
    if (stale()) return;
    setDefaultALoD({
      amount: quotationTemp.insured_add_amount,
      price: quotationTemp.quotations.find((q) => q.selected)?.rider_optional_total_add_in_frecuency || 0,
    });

    if (response.country === 'BR') {
      const requestBR = { ...request, policy_amount: Math.min(request.policy_amount, 400000), ad_amount: 50000, tpd_amount: 50000, lead: cleanLeadForUpsert(request.lead) };
      const quotationBR = await calcule(id, requestBR, REACT_APP_QUOTATION_API_KEY);
      if (stale()) return;
      const selectedQ = quotationBR.quotations.find((q) => q.selected);
      setDefaultAd({
        amount: quotationBR.insured_ad_amount || 50000,
        price: selectedQ?.rider_optional_total_ad_in_frecuency || 0,
      });
      setDefaultTpd({
        amount: quotationBR.insured_tpd_amount || 50000,
        price: selectedQ?.rider_optional_total_tpd_in_frecuency || 0,
      });
    }
  };
  runBootWritesRef.current = runBootWrites;

  const fetchQuote = async (quoteID: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getPreviousQuote(quoteID, REACT_APP_QUOTATION_API_KEY);
      const product = getProduct(response.insured_amount, response.country);
      setProduct(product);
      setQuote(response);
      syncIsTestFromQuote(response.is_test);
      const request = buildQuoteRequest(response, product);
      setSelected(request);
      setLastQuote(response.quotations.find((q) => q.selected));
      setAmountInclude(response?.coverage_include_amount);

      // The application GET is already in flight from syncApplicationInfo
      // (coalesced). A failed read stays `desconocido` and is not cached.
      // Signed and later statuses are `bloqueado`. pathnameRef, not the
      // closure, so a navigate that landed while this read was in flight is
      // the path we decide against.
      const writeMode = await beginQuoteWriteRead(quoteID);
      if (activeQuoteIdRef.current !== quoteID) return;
      const pathname = pathnameRef.current;
      // Opening /application only reads the stored quote. Anterior then
      // navigates to /quote inside the same provider; keep the writes and
      // run them from the pathname effect.
      if (writeMode === 'editable' && isApplicationPath(pathname)) {
        pendingBootRef.current = { quoteId: quoteID };
      } else if (pendingBootRef.current?.quoteId === quoteID) {
        pendingBootRef.current = null;
      }
      const skipBootCalcule = shouldSkipBootCalcule({
        pathname,
        quoteLoaded: true,
        writeMode,
      });

      // Upsert de arranque solo en México y solo en /quote (también /quote/).
      if (!skipBootCalcule) {
        await runBootWrites(
          quoteID,
          request,
          response,
          product,
          !shouldSkipBootUpsert(writeMode) && REACT_APP_COUNTRY === 'MX' && isQuotePath(window.location.pathname),
        );
      }
    } catch (error) {
      setError('Error al cargar la cotización.');
    } finally {
      setLoading(false);
    }
  };

  const applyLoadedQuote = (response: QuotationResponse): RefreshedQuote => {
    const nextProduct = getProduct(response.insured_amount, response.country);
    const request = buildQuoteRequest(response, nextProduct);
    const selectedQuotation = response.quotations.find((q) => q.selected);

    setProduct(nextProduct);
    setQuote(response);
    syncIsTestFromQuote(response.is_test);
    setSelected(request);
    setLastQuote(selectedQuotation);
    setAmountInclude(response?.coverage_include_amount);

    return { quote: response, selected: request, lastQuote: selectedQuotation };
  };

  const refreshQuote = async (): Promise<RefreshedQuote | null> => {
    if (!quoteID) return null;
    try {
      const response = await getPreviousQuote(quoteID, REACT_APP_QUOTATION_API_KEY);
      return applyLoadedQuote(response);
    } catch (error) {
      console.error('Error refreshing quote:', error);
      return null;
    }
  };

  // Re-read before the writes that were deferred on /application. refreshQuote
  // on /confirmation updates React state only; the upsert has to send the quote
  // the API has now, and it has to stop when the solicitud is no longer editable.
  const runDeferredBoot = async (id: string) => {
    const editEpoch = quoteEditEpochRef.current;
    // Fails after an edit (epoch), unmount, a new quote id, or leaving /quote,
    // so this boot does not publish. Leaving /quote restores the pending boot
    // for the next visit. An edit that is allowed to write clears that pending
    // boot. An edit that stops on an unknown or blocked status leaves it.
    const stillThisQuote = () =>
      mountedRef.current
      && activeQuoteIdRef.current === id
      && isQuotePath(pathnameRef.current)
      && quoteEditEpochRef.current === editEpoch;
    const leftWithoutEditing = () =>
      mountedRef.current
      && activeQuoteIdRef.current === id
      && quoteEditEpochRef.current === editEpoch
      && quoteLockedRef.current !== 'bloqueado';
    const restorePending = (reason: string, error?: unknown) => {
      if (!leftWithoutEditing()) return;
      if (pendingBootRef.current != null) return;
      pendingBootRef.current = { quoteId: id };
      captureMessageWithQuoteId('Deferred quote boot will retry', {
        level: 'warning',
        extra: {
          reason,
          ...(error == null ? {} : { error: error instanceof Error ? error.message : String(error) }),
        },
        tags: { error_type: 'deferred_boot_retry' },
      });
    };

    if (!mountedRef.current || activeQuoteIdRef.current !== id) return;
    let response: QuotationResponse;
    try {
      response = await getPreviousQuote(id, REACT_APP_QUOTATION_API_KEY);
    } catch (error) {
      restorePending('reread_failed', error);
      return;
    }
    if (quoteEditEpochRef.current !== editEpoch || !mountedRef.current || activeQuoteIdRef.current !== id) return;
    if (!isQuotePath(pathnameRef.current)) {
      restorePending('route_changed');
      return;
    }
    const writeMode = await beginQuoteWriteRead(id, true);
    if (quoteEditEpochRef.current !== editEpoch || !mountedRef.current || activeQuoteIdRef.current !== id) return;
    if (writeMode === 'bloqueado') return;
    if (!isQuotePath(pathnameRef.current)) {
      restorePending('route_changed');
      return;
    }
    if (writeMode !== 'editable') {
      restorePending('status_unknown');
      return;
    }
    const fresh = applyLoadedQuote(response);
    await runBootWrites(
      id,
      fresh.selected,
      response,
      getProduct(response.insured_amount, response.country),
      REACT_APP_COUNTRY === 'MX',
      stillThisQuote,
    );
  };
  runDeferredBootRef.current = runDeferredBoot;

  const calculation = async (formData: QuoteRequest) => {
    // The edit belongs to the quote on this render. activeQuoteIdRef moves as
    // soon as the URL changes, before this closure is replaced, so a click in
    // that gap must not upsert the next quote with this form.
    const id = quoteID;
    if (!id || activeQuoteIdRef.current !== id) return;
    quoteEditEpochRef.current += 1;
    const writeMode =
      quoteLockedRef.current === 'desconocido' ? await beginQuoteWriteRead(id) : quoteLockedRef.current;
    if (activeQuoteIdRef.current !== id || writeMode !== 'editable') return;
    // The edit is the write that remains. A boot that has not started yet
    // must not run on the next visit and upsert over this body. Drop it only
    // once this edit is allowed to write. An unknown or blocked status leaves
    // the pending boot for the next visit to /quote.
    if (writeMode === 'editable' && pendingBootRef.current?.quoteId === id) pendingBootRef.current = null;
    const requestId = ++latestCalculationRef.current;
    const isStale = () => requestId !== latestCalculationRef.current;

    setLoading(true);
    setCalculationError(false);
    try {
      // Para México, asegurar que term sea igual a term_selected antes del upsert
      if (formData.country === 'MX') {
        formData.term = formData.term_selected;
        formData.frecuency = formData.frecuency_selected;
      }

      // Limpiar additional_data si es null antes de enviar al upsert
      const cleanedFormData = {
        ...formData,
        lead: cleanLeadForUpsert(formData.lead)
      };

      // The deferred boot may already be inside upsert. Wait for that call to
      // settle, then send this edit, so the edit is the later write. The epoch
      // bump above makes the boot skip setQuote when its response arrives.
      // Skip the await when nothing is in flight: awaiting a missing promise
      // still yields, and two clicks in one turn would then consume each
      // other's upsert. A boot that never settles must not hold the spinner:
      // after 10s the edit continues, and the epoch still drops the late publish.
      // Clearing the ref when the timeout wins lets the next edit skip the wait.
      // The in-flight boot upsert is not cancelled, so a late one can still
      // leave the pre-edit body on the server.
      const deferredBoot = deferredBootRef.current;
      let waitedForBoot = false;
      if (deferredBoot) {
        waitedForBoot = true;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let bootTimedOut = false;
        try {
          await Promise.race([
            deferredBoot.catch(() => {}),
            new Promise<void>((resolve) => {
              timeoutId = setTimeout(() => {
                bootTimedOut = true;
                resolve();
              }, 10_000);
            }),
          ]);
        } finally {
          if (timeoutId !== undefined) clearTimeout(timeoutId);
        }
        if (bootTimedOut) {
          if (deferredBootRef.current === deferredBoot) deferredBootRef.current = null;
        }
        if (isStale() || activeQuoteIdRef.current !== id) return;
      }

      const result = await upsert(id, cleanedFormData, REACT_APP_QUOTATION_API_KEY);
      if (isStale()) return;
      const lastQuote = result.quotations.find((q) => q.selected);
      // `lastQuote.protections` is the API's CATALOG of options (every amount with its price),
      // not what was contracted — that is the top-level `protection`. Deriving the state from the
      // catalog resent protections the client never chose; deriving it from their premium switched
      // off the ones priced at 0.
      const contracted = result.protection?.amount ? result.protection : null;
      const cp_protections = contracted?.type === 'cp' ? [contracted] : [];
      const ci_protections = contracted?.type === 'ci' ? [contracted] : [];
      setProduct(result.product_type);
      // The upsert returns a zero-value created_at ('0001-01-01T00:00:00Z') while the GET returns
      // the real one, so adopting the response verbatim showed "31 DIC 1" after any edit.
      setQuote({ ...result, created_at: isRealDate(result.created_at) ? result.created_at : quote?.created_at });
      setSelected({
        // The quote keeps the engine version it was created with; the map is only a fallback,
        // since it has drifted behind the engine (it says v3 while INT quotes are created v5)
        // and a stale version makes the API reject the upsert on the engine foreign key.
        version: result.version || JSON_VERSIONS[result.product_type]?.[getTypeQuote(result.country)],
        product_type: result.product_type,
        country: result.country,
        country_origin: result.country_origin,
        // Read back what the API persisted: it normalises the term and omits the ones where
        // age + term > 85, so echoing the request would leave `selected` claiming a term the
        // quote does not have.
        term: result.term || formData.term,
        term_selected: lastQuote?.term || formData.term_selected,
        frecuency: result.frecuency || formData.frecuency,
        frecuency_selected: lastQuote?.frecuency || formData.frecuency_selected,
        gender: result.gender,
        smoker: result.smoker,
        age: result.age,
        policy_amount: result.insured_amount,
        add_amount: result.insured_add_amount === 0 ? null : result.insured_add_amount,
        ad_amount: result.insured_ad_amount === 0 ? null : result.insured_ad_amount || null,
        tpd_amount: result.insured_tpd_amount === 0 ? null : result.insured_tpd_amount || null,
        extra_premium_table_ad: result.extra_premium_table_add,
        rop: result.rop,
        lead: result.lead,
        annual_income: result.annual_income,
        income_replacement: result.income_replacement,
        cp_protections,
        ci_protections,
        protection: contracted,
        extra_premium_table_add: result.extra_premium_table_add,
        extra_premium_table_rating: result.extra_premium_table_rating,
        flat_extra_amount: result.flat_extra_amount,
        flat_extra_years: result.flat_extra_years,
        score: result.score,
        is_test: result.is_test,
      });
      setLastQuote(lastQuote);
      setAmountInclude(result?.coverage_include_amount);

      // The superseded boot returned before its reference-price calcule. Price
      // ALoD, and AD/TPD on a BR quote, from this edit. A preview failure leaves
      // those amounts at 0; the edit itself already landed.
      if (waitedForBoot) {
        try {
          const reference = {
            ...cleanedFormData,
            add_amount: REACT_APP_COUNTRY === 'MX' ? 50000 : 100000,
          };
          const quotationTemp = await calcule(id, reference, REACT_APP_QUOTATION_API_KEY);
          if (isStale() || activeQuoteIdRef.current !== id || !mountedRef.current) return;
          setDefaultALoD({
            amount: quotationTemp.insured_add_amount,
            price: quotationTemp.quotations.find((q) => q.selected)?.rider_optional_total_add_in_frecuency || 0,
          });
          if (cleanedFormData.country === 'BR') {
            const requestBR = {
              ...cleanedFormData,
              policy_amount: Math.min(cleanedFormData.policy_amount, 400000),
              ad_amount: 50000,
              tpd_amount: 50000,
            };
            const quotationBR = await calcule(id, requestBR, REACT_APP_QUOTATION_API_KEY);
            if (isStale() || activeQuoteIdRef.current !== id || !mountedRef.current) return;
            const selectedQ = quotationBR.quotations.find((q) => q.selected);
            setDefaultAd({
              amount: quotationBR.insured_ad_amount || 50000,
              price: selectedQ?.rider_optional_total_ad_in_frecuency || 0,
            });
            setDefaultTpd({
              amount: quotationBR.insured_tpd_amount || 50000,
              price: selectedQ?.rider_optional_total_tpd_in_frecuency || 0,
            });
          }
        } catch (_) {}
      } else if (result.country === 'BR' && result.insured_amount < BR_RIDERS_TOTAL_LIMIT) {
        // Si es BR y la cobertura principal bajó de 500k, recalcular precios de referencia de riders
        try {
          const requestBR = {
            ...cleanedFormData,
            policy_amount: 400000,
            ad_amount: 50000,
            tpd_amount: 50000,
          };
          const quotationBR = await calcule(id, requestBR, REACT_APP_QUOTATION_API_KEY);
          const selectedQ = quotationBR.quotations.find((q) => q.selected);
          setDefaultAd({
            amount: quotationBR.insured_ad_amount || 50000,
            price: selectedQ?.rider_optional_total_ad_in_frecuency || 0,
          });
          setDefaultTpd({
            amount: quotationBR.insured_tpd_amount || 50000,
            price: selectedQ?.rider_optional_total_tpd_in_frecuency || 0,
          });
        } catch (_) {}
      }
    } catch (error) {
      // A failed recalculation must not clear `error`'s consumer (quoteSummaryPage unmounts the
      // whole summary on `error`): the quote is still there, only the edit didn't apply.
      if (!isStale()) setCalculationError(true);

      captureMessageWithQuoteId('Error en cálculo de cotización', {
        level: 'error',
        extra: {
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString()
        },
        tags: {
          error_type: 'calculation_error'
        }
      });
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  return (
    <QuoteContext.Provider
      value={
        {
          quoteID,
          quote,
          calculation,
          selected,
          lastQuote,
          product,
          defaultALoD,
          defaultAd,
          defaultTpd,
          amountInclude,
          moreBenefits,
          setMoreBenefits,
          loading,
          setLoading,
          error,
          calculationError,
          dismissCalculationError: () => setCalculationError(false),
          indexOfPages,
          setIndexOfPages,
          specialPages,
          setSpecialPages,
          dependents,
          setDependents,
          beneficiaries,
          setBeneficiaries,
          redirectPageId,
          setRedirectPageId: (pageId: number | null) => {
            if (pageId === null) {
              localStorage.removeItem('redirectPageId');
            } else {
              localStorage.setItem('redirectPageId', pageId.toString());
            }
            setRedirectPageId(pageId);
          },
          startFromBeginning,
          setStartFromBeginning: (value: boolean) => {
            if (!value) {
              localStorage.removeItem('startFromBeginning');
            } else {
              localStorage.setItem('startFromBeginning', 'true');
            }
            setStartFromBeginning(value);
          },
          isTest,
          setIsTest,
          isTestLoading,
          refreshQuote,
          ridersOverLimit,
          setRidersOverLimit,
        }
      }
    >
      {children}
    </QuoteContext.Provider>
  );
};
